import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Box,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  AuditStateQuestion,
  MobileStateItemBase,
  MobileStateQuestion,
} from '@safeguardproperties/corescript-react-npm/dist/types';
import CustomTypography from '../../../ui/atoms/CustomTypography';
import { RootState } from '../../../redux/Store';
import {
  getScriptSave,
  setAccept,
  setAnswer,
  SetAnswerProps,
  setModifyAnswer,
} from '../../../redux/reducer/coreScriptReducer';
import { useDispatch, useSelector } from 'react-redux';
import TestQuestion from '../../Questions/TestQuestion';
import QuestionItemComponent from '../../Questions/Question';
import { toggleStation } from '../../../redux/reducer/selectedQuestionReducer';
import IconAcceptQuestion from '../../../assets/icons/IconAcceptQuestion';
import { useStationBadgeCount } from '../../../utils/helpers/formatValue';
import { useEffect, useMemo, useState } from 'react';
import { Platform } from '../../../constants/platform';

// region SX Section
export const headerCommonSx = {
  border: 'none',
  fontFamily: 'Montserrat',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '14.63px',
  textAlign: 'left',
};
// endregion

type QuestionGroupComponentProps = {
  data: MobileStateItemBase[] | AuditStateQuestion[] | any;
  title: string;
  index: number;
  aishow: boolean;
};

const QuestionGroupComponent = ({
  data,
  title,
  index,
  aishow = false,
}: QuestionGroupComponentProps) => {
  const isComingFromAudit = useSelector(
    (state: RootState) => state.contextId.isAudit
  );
  const checkboxState = useSelector(
    (state: RootState) => state.selectedQuestion.stations
  );
  const allSelected = useSelector(
    (state: RootState) => state.selectedQuestion.allSelected
  );
  const isReadOnly = useSelector(
    (state: RootState) => state.contextId.isReadOnly
  );
  const viewFlagged = useSelector(
    (state: RootState) => state.contextId.viewFlagged
  );
  const dispatch = useDispatch();

  const [isExpanded, setIsExpanded] = useState(true);

  const badgeStationCount = useStationBadgeCount(data);

  // Effect to handle automatic expansion/collapse based on viewFlagged
  useEffect(() => {
    // Automatically expand when viewFlagged is true
    // if (viewFlagged) {
    setIsExpanded(true);
    // }
    //  else {
    //   // Collapse when viewFlagged is false
    //   setIsExpanded(false);
    // }
  }, [viewFlagged]);

  const saveData = useSelector(getScriptSave);

  const setScriptAnswer = (questionKey: string, answers: string[]) => {
    dispatch(
      setAnswer({
        questionPath: questionKey,
        answers: answers,
      } as SetAnswerProps)
    );
  };

  const setDecisionAnswer = (
    questionKey: string,
    comments: string,
    answers: string[]
  ) => {
    dispatch(
      setModifyAnswer({
        questionPath: questionKey,
        answers,
        comments,
      })
    );
  };

  const handleStationToggle = (
    station: string,
    isChecked: boolean,
    questionKeys: string[]
  ) => {
    dispatch(toggleStation({ station, isChecked, questionKeys }));
  };

  const handleAcceptAll = () => {
    dispatch(
      setAccept({
        questionPaths: checkboxState[title],
      })
    );
    handleStationToggle(
      title,
      false,
      data
        ?.filter(
          (q: any) =>
            !q?.AuditDecision?.DecisionId && q?.Medallions !== undefined
        )
        ?.map((q: any) => q.QuestionKey) // Include only questions without DecisionId
    );
  };

  const checkForDisabled = () => {
    return (
      data?.every(
        (q: any) => q?.AuditDecision?.DecisionId || q?.Medallions === undefined
      ) ||
      isReadOnly ||
      allSelected ||
      (data?.length === 1 &&
        (saveData?.Answers[data[0]?.Title]?.Answers?.length === 0 ||
          saveData?.Answers[data[0]?.Title]?.Answers.every(
            (answer: any) => answer?.Answer === ''
          )))
      // ||
      // data?.evey((question: any) => question.Medallions === undefined)
    );
  };

  const handleAccordionChange = (
    event: React.SyntheticEvent,
    expanded: boolean
  ) => {
    setIsExpanded(expanded);
  };

  return (
    <Accordion
      key={index}
      expanded={isExpanded}
      onChange={handleAccordionChange}
      elevation={0}
      sx={{
        border: '1px solid #E3E3E3',
        borderRadius: '8px',
        margin: `${index == 0 ? '0px' : '10px'} 0`,
        '&::before': { height: '0px', backgroundColor: 'transparent' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box
          sx={{
            display: 'flex',
            flex: '1',
            justifyContent: 'space-between',
          }}
        >
          <CustomTypography
            variant='h5'
            sx={{
              color: '#343330',
              fontFamily: 'Montserrat',
              fontSize: '18px',
              fontStyle: 'normal',
              fontWeight: '600',
              textTransform: 'capitalize',
              lineHeight: '21.94px',
              textAlign: 'left',
              textUnderlinePosition: 'from-font',
              textDecorationSkipInk: 'none',
            }}
          >
            {title?.toLanguagePack()}
            {badgeStationCount > 0 && (
              <Box
                component='span'
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '20px',
                  height: '20px',
                  backgroundColor: '#F35625',
                  borderRadius: '50%',
                  color: '#fff',
                  fontSize: '11px',
                  marginLeft: '8px',
                  position: 'absolute',
                  top: '15%',
                }}
              >
                {badgeStationCount}
              </Box>
            )}
          </CustomTypography>

          {checkboxState[title]?.length > 0 && !allSelected && (
            <Button
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
              variant='text'
              onClick={(e) => {
                e.stopPropagation();
                handleAcceptAll();
              }}
            >
              <IconAcceptQuestion />
              <CustomTypography
                variant='body1'
                sx={{
                  color: '#1C6EC9',
                  fontFamily: 'Montserrat',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: '500',
                  lineHeight: '28px',
                  marginX: '0.3rem',
                  marginTop: '0.2rem',
                }}
              >
                Accept All
              </CustomTypography>
            </Button>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Table>
          <TableHead
            sx={{
              position: 'sticky',
              top: 0, // Adjust as needed based on scrolling
              zIndex: Platform?.isInspect() ? 1 : 1000, // Ensure it stays above other elements
            }}
          >
            <TableRow
              sx={{
                display: 'flex',
                flex: 8,
                alignItems: 'center',
                backgroundColor: '#EAEAEA',
                borderRadius: '8px 8px 0 0',
                height: '46px',
              }}
            >
              {isComingFromAudit && (
                <TableCell
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px',
                    border: 'none',
                  }}
                >
                  <Checkbox
                    color='primary'
                    disabled={checkForDisabled()}
                    checked={checkboxState[title]?.length > 0}
                    sx={{
                      width: '24px',
                      height: '24px',
                      '&.Mui-disabled': {
                        cursor: 'not-allowed !important',
                        pointerEvents: 'all !important',
                      },
                    }}
                    onChange={(e) =>
                      handleStationToggle(
                        title,
                        e.target.checked,
                        data
                          ?.filter(
                            (q: any) =>
                              !q?.AuditDecision?.DecisionId &&
                              q?.Medallions != undefined &&
                              saveData?.Answers[q?.Title]?.Answers?.length > 0
                          )
                          ?.map((q: any) => q.QuestionKey)
                      )
                    }
                  />
                </TableCell>
              )}

              <TableCell
                sx={{
                  ...headerCommonSx,
                  flex: isComingFromAudit ? 1.5 : 3,
                  padding: '8px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                Question
              </TableCell>

              <TableCell
                sx={{ ...headerCommonSx, flex: isComingFromAudit ? 1 : 3 }}
              >
                {isComingFromAudit ? 'Final' : 'Value'}
              </TableCell>

              {isComingFromAudit && (
                <TableCell
                  sx={{
                    ...headerCommonSx,
                    flex: 1,
                    padding: '12px',
                    border: 'none',
                  }}
                >
                  <CustomTypography
                    variant='body1'
                    sx={{ ...headerCommonSx, cursor: 'pointer' }}
                  >
                    Expected
                  </CustomTypography>
                </TableCell>
              )}

              {isComingFromAudit && aishow && (
                <TableCell
                  sx={{
                    ...headerCommonSx,
                    flex: 1,
                  }}
                >
                  AI
                </TableCell>
              )}

              <TableCell
                sx={{ ...headerCommonSx, flex: isComingFromAudit ? 0.5 : 1 }}
              >
                Images
              </TableCell>

              {isComingFromAudit && (
                <TableCell
                  sx={{
                    ...headerCommonSx,
                    flex: 2,
                  }}
                >
                  Action Taken
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((item: any, index: number) =>
              isComingFromAudit ? (
                <QuestionItemComponent
                  key={index}
                  question={item as AuditStateQuestion}
                  title={title}
                  index={index}
                  expanded={false}
                  answers={
                    saveData.Answers[(item as MobileStateQuestion).Title]
                  }
                  keyToString={(v) => v?.toLanguagePack()}
                  onSetAnswer={setDecisionAnswer}
                  aishow={aishow}
                />
              ) : (
                <TestQuestion
                  key={index}
                  question={item}
                  index={index}
                  answers={
                    saveData.Answers[(item as MobileStateQuestion).Title]
                  }
                  keyToString={(v) => v?.toLanguagePack()}
                  onSetAnswer={setScriptAnswer}
                />
              )
            )}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  );
};

export default QuestionGroupComponent;
